/**
 * Hook dispatch: the one place a job's state change turns into behaviour.
 *
 * Two rules, both deliberate:
 *  1. Hooks fire AFTER the write. A hook that reads the job back sees the state it is being told about.
 *  2. A hook can never break the runner or the job. Failures are logged and swallowed — a progress hook
 *     throwing must not turn a finished extraction into a failed one.
 *
 * PER TYPE ONLY. There used to be a registry of cross-cutting `JobHooks` alongside this, added via
 * `addGlobalJobHooks`, for "progress mirroring, metrics, surfacing a terminal failure to the teacher". Nothing ever
 * registered one — every behaviour that arrived turned out to be per type, because what a transition MEANS depends
 * on which step it belongs to. The array, its adder and its test reset went with it, and later the `JobHooks`
 * interface itself; a global concern can be a `defineJob` field or live here explicitly.
 *
 * TWO HOOKS, AND THE DIFFERENCE BETWEEN THEM IS THE POINT:
 *
 *  - `onTransition` is about THIS ROW. It fires on every state change, and the definition does its own filtering.
 *  - `onStepComplete` is about the STEP. This file asks the one question that decides it — does the exam still have
 *    a live row of this type's step? — and the definition only declares what should then happen. The question is asked
 *    for EVERY type, declaration or not, because a drained wave also has to close its batch.
 *
 * That second hook used to be each type's job to wire: `onTransition: setupStepSync(theClosure)`, applied by hand at
 * seven call sites, each closure restating its own step name. The predicate, the terminal-status check and the
 * ordering were duplicated into every one of them by a wrapper that had to be remembered. Now they are here, once.
 *
 * IT FIRES ON `failed` AND `cancelled` AS WELL AS `done`, deliberately. A wave whose last member failed is still a
 * finished wave — a step has already written its own ERROR where it had one — and a completion that only counted
 * successes would leave the progress bar spinning forever on exactly the exams that need attention most.
 */

import { logger } from '@adornis/base/logging.js';
import { isTerminalJobStatus, type JobTransition } from './job.js';
import { getJobDefinition } from './registry.js';

const safely = async (label: string, fn: () => void | Promise<void>): Promise<void> => {
  try {
    await fn();
  } catch (err) {
    logger.error({ err, hook: label }, 'Job hook failed — ignored');
  }
};

export async function emitJobTransition(event: JobTransition): Promise<void> {
  const { type, examId } = event.job;
  const definition = getJobDefinition(type);
  if (!definition) return;

  // Called on the definition rather than pulled off it: `onTransition` is a method signature (it has to be — as a
  // property its parameter turns contravariant and `JobDefinition<P>` stops being assignable to
  // `JobDefinition<JobPayload>`), so extracting it would be an unbound method.
  if (definition.onTransition) await safely(`${type}.onTransition`, () => definition.onTransition?.(event));

  if (!isTerminalJobStatus(event.to)) return;

  // IMPORTED HERE rather than at the top, and not for laziness: `job-store.ts` imports this module to emit its
  // transitions, and `step-completion.ts` imports the store to ask whether the step is drained. A static import
  // would close that ring; the machinery is reached only from this one line, so the dynamic form costs a cached
  // module lookup and buys a dependency graph that still points one way.
  //
  // ASKED ONCE, FOR EVERY TYPE — including the ones that declare no `onStepComplete`. A wave still has to CLOSE its
  // batch when it ends, or the next dispatch joins a batch belonging to work that finished long ago and the two waves
  // announce as one. `structure` and `submissionSplit` reach only that half.
  await safely(`${type}.onStepComplete`, async () => {
    const { finishStepWave } = await import('./step-completion.js');
    // The ROW's own batch, so it closes the wave it belonged to rather than whatever happens to be open now.
    await finishStepWave(type, definition.onStepComplete, examId, event.job.batchKey);
  });
}
